"use client";
import React, { FC, useMemo, useState } from "react";
import SVGIcon from "../../SVGIcon";
import Image from "next/image";
import PromotionGameList from "./PromotionGameList";
import PromotionDescription from "./PromotionDescription";
import Timer from "./Timer";
import { IPromotionsAttributes } from "@/fe-core/meta/interfaces/promotions";
import { Link } from "../../../navigation";
import { useScreen } from "../../../context/ScreenContext";
import { useSelector } from "react-redux";
import { themeSelector } from "@/fe-core/_redux/selectors/themeSelectors";
import { useTranslations } from "next-intl";
import { I18N_NAMESPACE } from "@/config/i18n";
import useTracking from "@/components/Hooks/useTracking";
import { Transition } from "@headlessui/react";
import classNames from "classnames";

type PromotionBonusDetailsProps = {
  promotion: IPromotionsAttributes;
};

const PromotionBonusDetails: FC<PromotionBonusDetailsProps> = ({
  promotion,
}) => {
  const [showTerms, setShowTerms] = useState(false);
  const { navigate } = useTracking();
  const t = useTranslations(I18N_NAMESPACE.PROMOTIONS);
  const { isMobile } = useScreen();
  const theme = useSelector(themeSelector);
  const promotionNavBar = () => {
    return (
      <>
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div className="flex items-center gap-2 px-4 py-2.5 text-sm font-normal bg-white rounded text-tertiary/70 dark:bg-tertiary-700 dark:text-white/70">
            <Link
              href={"/promotions"}
              onClick={() => {
                navigate(`/promotions`, {
                  component: "PromotionDetailPage - PromotionBonusDetails",
                  label: t("bonus-details"),
                  navigateFrom: "Promotion Detail page",
                });
              }}
            >
              <span className="capitalize">{t("promotions")}</span>
            </Link>
            <SVGIcon
              icon={"angleArrow"}
              className="block w-3 h-3 text-tertiary/50 dark:text-white/50"
            />
            <span className="capitalize text-tertiary dark:text-white">
              {t("bonus-details")}
            </span>
          </div>
        </div>
      </>
    );
  };

  const endDate = useMemo(() => {
    return promotion?.promotionDuration?.dateTo;
  }, [promotion]);

  const imgURL = useMemo(() => {
    const imgURL = isMobile
      ? // @ts-ignore
        promotion &&
        // @ts-ignore
        promotion[`mobileImage${theme.theme}`]?.data?.attributes?.url
      : // @ts-ignore
        promotion &&
        // @ts-ignore
        promotion[`desktopImage${theme.theme}`]?.data?.attributes?.url;
    return imgURL;
  }, [promotion, theme]);

  return (
    <div className="container relative pt-10">
      <div className="flex flex-col gap-6 md:gap-10">
        <div>
          {promotionNavBar()}
          <div className="relative flex items-center justify-center   aspect-[390/193] w-full rounded-lg overflow-auto">
            <Image
              src={`${process.env.NEXT_PUBLIC_CMS_URL}${imgURL}`}
              objectFit="cover"
              fill
              alt="promotions-banner"
              className="w-full h-full"
              loading="lazy"
            />
          </div>
        </div>
        <div>
          <h3 className="text-tertiary dark:text-tertiary-50 text-2.5xl font-bold mb-4">
            {promotion?.title}
          </h3>
          {endDate && new Date(endDate).getTime() >= new Date().getTime() && (
            <div className="px-2 py-3 text-center bg-white rounded md:p-4 dark:bg-tertiary-700">
              <Timer endTime={endDate} />
            </div>
          )}
        </div>

        {promotion?.content && (
          <PromotionDescription content={promotion?.content} />
        )}

        {promotion?.eligibleGameIds &&
          promotion?.eligibleGameIds?.length > 0 && (
            <PromotionGameList promotion={promotion} />
          )}

        {promotion?.termsAndConditions && (
          <div>
            <div
              className="flex items-center justify-between w-full p-4 py-2 text-sm rounded cursor-pointer bg-tertiary-700 group"
              onClick={() => setShowTerms(!showTerms)}
            >
              {t("promotions-terms-and-conditions")}
              <span
                className={classNames("text-3xl transition-all", {
                  "rotate-90": showTerms,
                })}
              >
                +
              </span>
            </div>

            <Transition show={showTerms}>
              <div
                dangerouslySetInnerHTML={{
                  __html: promotion?.termsAndConditions,
                }}
                className="prose-sm p-4 py-2 prose prose-invert opacity-60 max-w-none"
              />
            </Transition>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionBonusDetails;
